import ICONS from "../../../assets/Icons";
import TEXT from "../../../utils/globalText.js";
import Loader from "../../Loader/Index.jsx";
import CommonCalendar from "../CommonCalendar/index.jsx";
import MultiSelectDropDownComponent from "../CommonDropdown/MultiSelectDropdown/index.jsx";
import CommonTimePicker from "../CommonDropdown/SetTime/index.jsx";
import SingleSelectDropDownComponent from "../CommonDropdown/SingleSelectDropdown/index.jsx";
import CommonFileUpload from "../CommonFileUpload/index.jsx";
import MultilineInputField from "../CommonInputFields/MultilineInputField.jsx";
import TextInputField from "../CommonInputFields/TextInputField.jsx";
import CommonQuantity from "../CommonQuantity/index.jsx";
import CommonToggleCard from "./CommonToggleCard.jsx";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const getYear = 1975;
const minLength = 1;
const currentYear = new Date().getFullYear();
const years = Array.from(
  { length: currentYear - getYear + minLength },
  (_, i) => getYear + i,
);

const formatUIDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getDate()} ${d.toLocaleString("en-US", { month: "short" })}, ${d.getFullYear()}`;
};

/**
 * @typedef {Object} DynamicAdditionalFieldsProps
 *
 * @property {Array<Object>} mergeCategory
 * List of additional field configurations received from backend.
 * Each item defines `name`, `data_type`, `mandatory`, `description`, and `data`.
 *
 * @property {Object<string, any>} additionalData
 * Stores values entered or selected for additional fields.
 *
 * @property {(updater: Function) => void} setAdditionalData
 * State setter for `additionalData`.
 *
 * @property {Object<string, string>} hyperLink
 * Stores hyperlink values keyed by `${fieldName}_${dataType}`.
 *
 * @property {(updater: Function) => void} setHyperLink
 * State setter for hyperlink fields.
 *
 * @property {Object<string, string>} timeData
 * Stores selected time values for `time` fields.
 *
 * @property {(updater: Function) => void} setTimeData
 * State setter for time fields.
 *
 * @property {Object<string, {from: string, to: string}>} selectedValue
 * Stores values for `from_year_to_year` fields.
 *
 * @property {(updater: Function) => void} setSelectedValue
 * State setter for year-range fields.
 *
 * @property {Object<string, {month: string, year: string}>} selectedValueMonth
 * Stores values for `month_year` fields.
 *
 * @property {(updater: Function) => void} setSelectedValueMonth
 * State setter for month-year fields.
 *
 * @property {Object<string, number>} quantities
 * Stores values for `quantity` fields.
 *
 * @property {(updater: Function) => void} setQuantities
 * State setter for quantity fields.
 *
 * @property {Object<string, Array|Object>} uploadedFiles
 * Stores uploaded file metadata for `upload` fields.
 *
 * @property {(updater: Function) => void} setUploadedFiles
 * State setter for uploaded files.
 *
 * @property {Object<string, Date>} startDate
 * Stores start date values for `date` and `date_range` fields.
 *
 * @property {(updater: Function) => void} setStartDate
 * State setter for start dates.
 *
 * @property {Object<string, Date>} endDate
 * Stores end date values for `date_range` fields.
 *
 * @property {(updater: Function) => void} setEndDate
 * State setter for end dates.
 *
 * @property {Object<string, Object>} initialRefactorAdditionalDetail
 * Stores intermediate metadata such as paste/copy state for fields.
 *
 * @property {(updater: Function) => void} setInitialRefactorAdditionalDetail
 * State setter for refactor metadata.
 *
 * @property {(fieldName: string, value: any, dataType: string) => void} onInputChanged
 * Callback triggered whenever an additional field value changes.
 * Used to sync additional field data with the parent form.
 *
 * @property {(updater: Function) => void} [setPreviewImages]
 * State setter for uploaded image preview data.
 *
 * @property {boolean} [isCategoryLoading=false]
 * Indicates that category-specific additional fields are being loaded.
 * Displays the loader while true.
 *
 * @property {string} [headerText]
 * Optional heading displayed at the top of the additional details card.
 *
 * @property {boolean} [requiresCategorySelection=false]
 * Determines whether a category must be selected before displaying
 * category-specific additional fields.
 * Set to `true` for Beneficiary and Scheduled Beneficiary flows.
 * Keep `false` for flows such as Internal and Scheduled Internal where
 * category selection is not required.
 *
 * @property {boolean} [isCategorySelected=false]
 * Indicates whether a category has been selected.
 * Used only when `requiresCategorySelection` is enabled.
 */

const DynamicAdditionalFields = ({
  mergeCategory,
  additionalData,
  setAdditionalData,
  hyperLink,
  setHyperLink,
  timeData,
  setTimeData,
  quantities,
  setQuantities,
  uploadedFiles,
  setUploadedFiles,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  selectedValue,
  setSelectedValue,
  selectedValueMonth,
  setSelectedValueMonth,
  initialRefactorAdditionalDetail,
  setInitialRefactorAdditionalDetail,
  onInputChanged,
  setPreviewImages,
  isCategoryLoading,
  headerText,
  requiresCategorySelection = false,
  isCategorySelected = false,
}) => {
  const clearAdditionalData = (name, dataType) => {
    setAdditionalData((p) => {
      const c = { ...p };
      delete c[name];
      return c;
    });

    setInitialRefactorAdditionalDetail?.((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        isPaste: {},
        isCopyClick: false,
        isCopyDirect: false,
      },
    }));

    const key = `${name}_${dataType}`;
    if (dataType === "month_year")
      setSelectedValueMonth((p) => ({ ...p, [key]: { month: "", year: "" } }));
    if (dataType === "date" || dataType === "date_range")
      setStartDate((p) => ({ ...p, [key]: null }));
    if (dataType === "date_range") {
      setStartDate((p) => ({ ...p, [key]: null }));
      setEndDate((p) => ({ ...p, [key]: null }));
    }
    if (dataType === "quantity") setQuantities((p) => ({ ...p, [key]: 0 }));
    if (dataType === "from_year_to_year")
      setSelectedValue((p) => ({ ...p, [key]: { from: "", to: "" } }));
    if (dataType === "time") setTimeData((p) => ({ ...p, [key]: "" }));

    onInputChanged(name, "", dataType);
  };

  return (
    <div className="bg-primary-card py-padding-md h-auto flex flex-col px-padding-md items-start rounded-border-radius-md">
      {headerText && (
        <div className="w-width-full text-text-primary font-semibold text-font-md">
          <p className="text-start pb-padding-md">{headerText}</p>
        </div>
      )}
      {isCategoryLoading ? (
        <div className="w-full flex items-center justify-center">
          <Loader
            height={"var(--icon-lg)"}
            width={"var(--icon-lg)"}
          />
        </div>
      ) : requiresCategorySelection && !isCategorySelected ? (
        <p className="text-text-secondary text-font-sm">
          Select a category to fetch the category-specific details.
        </p>
      ) : !mergeCategory?.length ? (
        <div className="text-left text-text-secondary text-sm">
          {TEXT.ADDITIONAL_DETAILS_UNSUPPORTED}
        </div>
      ) : (
        <div className="flex flex-col gap-gap-md w-full">
          {mergeCategory.map((field, i) => {
            const key = `${field.name}_${field.data_type}`;
            const optionalLabel = field.mandatory === "Y" ? "" : " (optional)";
            const label = `${field.name}${optionalLabel}`;
            const value = additionalData?.[field.name];

            switch (field.data_type) {
              case "string":
              case "numeric": {
                const pasteValue =
                  initialRefactorAdditionalDetail?.[field.name]?.isPaste?.value;

                return (
                  <MultilineInputField
                    key={key}
                    name={field.name}
                    value={value || ""}
                    placeholder={label}
                    fontKind="big"
                    bgColor="var(--primary-card)"
                    fgColor="var(--text-primary)"
                    isFullWidth
                    adornment={pasteValue ? ICONS?.PASTE_WHITE : ""}
                    onAdornmentClick={
                      pasteValue
                        ? () => {
                            if (
                              field.data_type === "numeric" &&
                              !/^\d+$/.test(pasteValue)
                            ) {
                              return;
                            }

                            const finalValue =
                              field.data_type === "numeric"
                                ? String(pasteValue)
                                : pasteValue;
                            setAdditionalData((p) => ({
                              ...p,
                              [field.name]: finalValue,
                            }));

                            setInitialRefactorAdditionalDetail((p) => ({
                              ...p,
                              [field.name]: { ...p[field.name], isPaste: {} },
                            }));

                            onInputChanged(
                              field.name,
                              finalValue,
                              field.data_type,
                            );
                          }
                        : undefined
                    }
                    onInput={(e) => {
                      const v = e.target.value;

                      if (field.data_type === "numeric") {
                        if (v === "") {
                          clearAdditionalData(field.name);

                          setInitialRefactorAdditionalDetail?.((prev) => {
                            if (!prev?.[field.name]) return prev;

                            return {
                              ...prev,
                              [field.name]: {
                                ...prev[field.name],
                                isPaste: {},
                                isCopyClick: false,
                                isCopyDirect: false,
                              },
                            };
                          });

                          return;
                        }

                        if (!/^\d+$/.test(v)) return;

                        onInputChanged(field.name, String(v), field.data_type);

                        return;
                      }

                      if (!v) {
                        clearAdditionalData(field.name);

                        setInitialRefactorAdditionalDetail?.((prev) => {
                          if (!prev?.[field.name]) return prev;

                          return {
                            ...prev,
                            [field.name]: {
                              ...prev[field.name],
                              isPaste: {},
                              isCopyClick: false,
                              isCopyDirect: false,
                            },
                          };
                        });

                        return;
                      }

                      onInputChanged(field.name, v, field.data_type);
                    }}
                  />
                );
              }

              case "list":
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <SingleSelectDropDownComponent
                      options={field.data || []}
                      selected={value || ""}
                      placeholder={TEXT.SELECT}
                      preserveCase
                      setSelected={(v) =>
                        v
                          ? onInputChanged(field.name, v, field.data_type)
                          : clearAdditionalData(field.name)
                      }
                      onClear={() =>
                        clearAdditionalData(field.name, field.data_type)
                      }
                    />
                  </div>
                );

              case "hyperlink": {
                const v = hyperLink?.[key] || "";
                return (
                  <TextInputField
                    key={key}
                    value={value}
                    placeholder={label}
                    bgColor="var(--primary-card)"
                    fgColor="var(--text-primary)"
                    isFullWidth
                    adornment={v ? ICONS.CLOSE : ""}
                    onAdornmentClick={
                      v
                        ? () => {
                            setHyperLink((p) => {
                              const c = { ...p };
                              delete c[key];
                              return c;
                            });
                            clearAdditionalData(field.name);

                            setInitialRefactorAdditionalDetail?.((prev) => ({
                              ...prev,
                              [field.name]: {
                                ...prev[field.name],
                                isCopyClick: false,
                                isCopyDirect: false,
                                isPaste: {},
                              },
                            }));
                          }
                        : undefined
                    }
                    onInput={(e) => {
                      const val = e.target.value;
                      setHyperLink((p) => ({ ...p, [key]: val }));
                      val
                        ? onInputChanged(field.name, val, field.data_type)
                        : clearAdditionalData(field.name);
                    }}
                  />
                );
              }

              case "time": {
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <CommonTimePicker
                      keyTime={key}
                      value={value || ""}
                      fieldname={field.name}
                      data_type={field.data_type}
                      timeData={timeData}
                      setTimeData={setTimeData}
                      handleTimeConfirm={(name, type, h, m, mer) =>
                        onInputChanged(name, `${h}:${m} ${mer}`, type)
                      }
                      handleClearTime={() => clearAdditionalData(field.name)}
                    />
                  </div>
                );
              }

              case "day":
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <SingleSelectDropDownComponent
                      options={days}
                      selected={value || ""}
                      placeholder={TEXT.SELECT}
                      preserveCase
                      setSelected={(v) =>
                        v
                          ? onInputChanged(field.name, v, field.data_type)
                          : clearAdditionalData(field.name)
                      }
                      onClear={() =>
                        clearAdditionalData(field.name, field.data_type)
                      }
                    />
                  </div>
                );

              case "toggle":
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <CommonToggleCard
                      description={field.description}
                      state={!!value}
                      handleClick={() => {
                        const next = !value;
                        next
                          ? onInputChanged(field.name, next, field.data_type)
                          : clearAdditionalData(field.name);
                      }}
                    />
                  </div>
                );

              case "month":
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <SingleSelectDropDownComponent
                      options={months}
                      selected={value || ""}
                      placeholder={TEXT.SELECT}
                      preserveCase
                      setSelected={(v) =>
                        v
                          ? onInputChanged(field.name, v, field.data_type)
                          : clearAdditionalData(field.name)
                      }
                      onClear={() =>
                        clearAdditionalData(field.name, field.data_type)
                      }
                    />
                  </div>
                );

              case "upload": {
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <CommonFileUpload
                      id={i}
                      files={uploadedFiles?.[key] || []}
                      setFiles={(file) => {
                        if (
                          !file ||
                          (Array.isArray(file) && file.length === 0)
                        ) {
                          setUploadedFiles((prev) => {
                            const c = { ...prev };
                            delete c[key];
                            return c;
                          });

                          setAdditionalData((prev) => {
                            const c = { ...prev };
                            delete c[field.name];
                            return c;
                          });

                          onInputChanged(field.name, "", field.data_type);
                          return;
                        }

                        const fileObj = Array.isArray(file) ? file[0] : file;

                        setUploadedFiles((prev) => ({
                          ...prev,
                          [key]: fileObj,
                        }));

                        if (fileObj?.type?.startsWith("image/")) {
                          const reader = new FileReader();
                          reader.onload = () => {
                            setPreviewImages?.((prev) => ({
                              ...prev,
                              [key]: reader.result,
                            }));
                          };
                          reader.readAsDataURL(fileObj);
                        }

                        onInputChanged(field.name, fileObj, field.data_type);
                      }}
                    />
                  </div>
                );
              }

              case "date": {
                const d = startDate?.[key];
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <CommonCalendar
                      selectionType="single"
                      startDate={d}
                      type="big"
                      handleDateChange={(val) => {
                        if (!(val instanceof Date) || isNaN(val.getTime())) {
                          clearAdditionalData(field.name);
                          setStartDate((p) => {
                            const c = { ...p };
                            delete c[key];
                            return c;
                          });
                          onInputChanged(field.name, "", field.data_type);
                          return;
                        }
                        setStartDate((p) => ({ ...p, [key]: val }));
                        onInputChanged(
                          field.name,
                          formatUIDate(val),
                          field.data_type,
                        );
                      }}
                    />
                  </div>
                );
              }

              case "multi_select_list":
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <MultiSelectDropDownComponent
                      options={field.data || []}
                      selected={Array.isArray(value) ? value : []}
                      placeholder={TEXT.SELECT}
                      setSelected={(v) =>
                        onInputChanged(field.name, v || [], field.data_type)
                      }
                      onClear={() =>
                        onInputChanged(field.name, [], field.data_type)
                      }
                    />
                  </div>
                );

              case "year":
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <SingleSelectDropDownComponent
                      options={years.map(String)}
                      selected={value ? String(value) : ""}
                      placeholder={TEXT.SELECT}
                      preserveCase
                      setSelected={(v) =>
                        v
                          ? onInputChanged(field.name, v, field.data_type)
                          : clearAdditionalData(field.name)
                      }
                      onClear={() =>
                        clearAdditionalData(field.name, field.data_type)
                      }
                    />
                  </div>
                );

              case "quantity": {
                const qty = quantities?.[key] || 0;
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <CommonQuantity
                      quantity={qty}
                      setQuantity={(v) => {
                        setQuantities((p) => ({ ...p, [key]: v }));
                        v
                          ? onInputChanged(field.name, v, field.data_type)
                          : clearAdditionalData(field.name);
                      }}
                    />
                  </div>
                );
              }

              case "from_year_to_year": {
                const v = selectedValue?.[key] || { from: "", to: "" };

                const fromYear = v.from !== "" ? Number(v.from) : "";
                const toYear = v.to !== "" ? Number(v.to) : "";

                const fromOptions = toYear
                  ? years.filter((y) => y < toYear).map(String)
                  : years.map(String);

                const toOptions = fromYear
                  ? years.filter((y) => y > fromYear).map(String)
                  : years.map(String);

                return (
                  <div
                    className="w-width-full"
                    key={key}
                  >
                    <div className="flex flex-row gap-gap-md w-width-full">
                      <div className="w-width-md flex flex-col gap-gap-xs">
                        <p className="text-text-secondary text-font-xs">
                          {label}
                        </p>
                        <SingleSelectDropDownComponent
                          options={fromOptions}
                          selected={v.from}
                          placeholder={TEXT.SELECT}
                          preserveCase
                          setSelected={(f) => {
                            if (!f) {
                              const next = { from: "", to: v.to || "" };

                              setSelectedValue((p) => ({ ...p, [key]: next }));
                              onInputChanged(field.name, next, field.data_type);
                              return;
                            }

                            const next = {
                              from: f,
                              to: v.to && Number(v.to) > Number(f) ? v.to : "",
                            };

                            setSelectedValue((p) => ({ ...p, [key]: next }));
                            onInputChanged(field.name, next, field.data_type);
                          }}
                        />
                      </div>

                      <div className="w-width-md flex flex-col gap-gap-xs">
                        <p className="text-text-secondary text-font-xs">
                          {label}
                        </p>
                        <SingleSelectDropDownComponent
                          options={toOptions}
                          selected={v.to}
                          placeholder={TEXT.SELECT}
                          preserveCase
                          setSelected={(t) => {
                            if (!t) {
                              const next = { from: v.from || "", to: "" };

                              setSelectedValue((p) => ({ ...p, [key]: next }));
                              onInputChanged(field.name, next, field.data_type);
                              return;
                            }

                            const next = {
                              from:
                                v.from && Number(v.from) < Number(t)
                                  ? v.from
                                  : "",
                              to: t,
                            };

                            setSelectedValue((p) => ({ ...p, [key]: next }));
                            onInputChanged(field.name, next, field.data_type);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              case "month_year": {
                const v = selectedValueMonth?.[key] || { month: "", year: "" };
                return (
                  <div className="w-width-full">
                    <div className="flex flex-row gap-gap-md w-width-full">
                      <div className="w-width-md flex flex-col gap-gap-xs">
                        <p className="text-text-secondary text-font-xs">
                          {label}
                        </p>
                        <SingleSelectDropDownComponent
                          options={months}
                          selected={v.month}
                          placeholder={TEXT.SELECT}
                          preserveCase
                          setSelected={(m) => {
                            const u = { ...v, month: m || "" };
                            setSelectedValueMonth((p) => ({ ...p, [key]: u }));
                            u.month && u.year
                              ? onInputChanged(field.name, u, field.data_type)
                              : clearAdditionalData(field.name);
                          }}
                        />
                      </div>

                      <div className="w-width-md flex flex-col gap-gap-xs">
                        <p className="text-text-secondary text-font-xs">
                          {label}
                        </p>
                        <SingleSelectDropDownComponent
                          options={years.map(String)}
                          selected={v.year}
                          placeholder={TEXT.SELECT}
                          preserveCase
                          setSelected={(y) => {
                            const u = { ...v, year: y || "" };
                            setSelectedValueMonth((p) => ({ ...p, [key]: u }));
                            u.month && u.year
                              ? onInputChanged(field.name, u, field.data_type)
                              : clearAdditionalData(field.name);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              case "date_range": {
                const s = startDate?.[key];
                const e = endDate?.[key];
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-gap-xs"
                  >
                    <p className="text-text-secondary text-font-xs">{label}</p>
                    <CommonCalendar
                      selectionType="range"
                      startDate={s}
                      endDate={e}
                      type="big"
                      handleDateChange={(dates) => {
                        if (!dates?.[0] || !dates?.[1]) {
                          clearAdditionalData(field.name, field.data_type);
                          return;
                        }
                        const [sd, ed] = dates;
                        setStartDate((p) => ({ ...p, [key]: sd }));
                        setEndDate((p) => ({ ...p, [key]: ed }));
                        onInputChanged(field.name, {
                          start_date: formatUIDate(sd),
                          end_date: formatUIDate(ed),
                        });
                      }}
                    />
                  </div>
                );
              }

              default:
                return null;
            }
          })}
        </div>
      )}
    </div>
  );
};

export default DynamicAdditionalFields;
